import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Alert, Row, Col, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSave, 
  faTimes, 
  faExclamationTriangle,
  faSpinner 
} from '@fortawesome/free-solid-svg-icons';
import { inventoryService } from '../../services/firestore';
import { useTranslation } from 'react-i18next';
import { useSubscription } from '../../contexts/SubscriptionContext';

const PART_TYPES = [
  { value: 'schermo', translationKey: 'models.part.types.schermo' },
  { value: 'batteria', translationKey: 'models.part.types.batteria' },
  { value: 'connettore', translationKey: 'models.part.types.connettore' },
  { value: 'fotocamera', translationKey: 'models.part.types.fotocamera' },
  { value: 'autoparlante', translationKey: 'models.part.types.autoparlante' },
  { value: 'cassa', translationKey: 'models.part.types.cassa' },
  { value: 'microfono', translationKey: 'models.part.types.microfono' }
];

function PartForm({ show, onHide, onSave, part = null }) {
  const { t } = useTranslation();
  const { 
    canCreatePart,
    subscription, 
    loading: subscriptionLoading,
    getRemainingParts,
    incrementPartCount
  } = useSubscription();

  const [formData, setFormData] = useState({
    brand: '',
    model: '',
    type: '',
    quantity: '',
    minThreshold: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [subscriptionError, setSubscriptionError] = useState(null);
  const [checkingSubscription, setCheckingSubscription] = useState(false);

  useEffect(() => {
    if (show) {
      if (part) {
        setFormData(part);
        // Reset degli errori di abbonamento quando si modifica una parte esistente
        setSubscriptionError(null);
      } else {
        setFormData({
          brand: '',
          model: '',
          type: '',
          quantity: '',
          minThreshold: ''
        });
        
        // Verifica lo stato dell'abbonamento quando si apre il form per una nuova parte
        checkSubscriptionStatus();
      }
    }
  }, [show, part]);

  // Funzione per verificare lo stato dell'abbonamento
  const checkSubscriptionStatus = async () => {
    if (!part) {  // Solo per nuovi ricambi
      try {
        // Non mostriamo più l'indicatore di caricamento nel pulsante
        // setCheckingSubscription(true);
        const result = await canCreatePart();
        
        if (!result.canCreate) {
          setSubscriptionError(result.reason);
        } else {
          setSubscriptionError(null);
        }
      } catch (error) {
        console.error('Errore nella verifica dell\'abbonamento:', error);
        setSubscriptionError(t('settings.subscription.errors.verificationFailed'));
      } finally {
        // Non aggiorniamo più lo stato di verifica
        // setCheckingSubscription(false);
      }
    }
  };

  const handleChange = (e) => {
    const { name, value, type: inputType } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: inputType === 'number' ? (value === '' ? '' : Number(value)) : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Se è un nuovo ricambio, verifica lo stato dell'abbonamento
    if (!part) {
      try {
        // Non mostriamo più l'indicatore di caricamento nel pulsante
        // setCheckingSubscription(true);
        const result = await canCreatePart();
        
        if (!result.canCreate) {
          setSubscriptionError(result.reason);
          // setCheckingSubscription(false);
          return;
        }
      } catch (error) {
        console.error('Errore nella verifica dell\'abbonamento:', error);
        setSubscriptionError(t('settings.subscription.errors.verificationFailed'));
        // setCheckingSubscription(false);
        return;
      } finally {
        // Non aggiorniamo più lo stato di verifica
        // setCheckingSubscription(false);
      }
    }

    setLoading(true);

    // Validazione dei dati prima del salvataggio
    const validatedData = {
      ...formData,
      brand: formData.brand.trim(),
      model: formData.model.trim(),
      type: formData.type.trim(),
      quantity: parseInt(formData.quantity, 10)
    };

    // Controlli di validazione
    if (!validatedData.brand || !validatedData.model || !validatedData.type) {
      setError('Tutti i campi sono obbligatori');
      setLoading(false);
      return;
    }

    if (isNaN(validatedData.quantity) || validatedData.quantity < 0) {
      setError('La quantità deve essere un numero valido maggiore o uguale a 0');
      setLoading(false);
      return;
    }

    try {
      const savedPart = await inventoryService.create(validatedData);
      if (savedPart) {
        // Incrementa il conteggio dei ricambi dopo il salvataggio
        if (!part) {
          await incrementPartCount();
        }
        onSave(savedPart);
        onHide();
        // Reset form
        setFormData({
          brand: '',
          model: '',
          type: '',
          quantity: '',
          minThreshold: ''
        });
      } else {
        throw new Error('Errore nel salvataggio del ricambio');
      }
    } catch (error) {
      console.error('Error saving part:', error);
      setError('Errore nel salvataggio del ricambio: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide}>
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>{part ? t('inventory.editPart') : t('inventory.addPart')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}

          {/* Avviso per lo stato dell'abbonamento */}
          {!part && subscription && subscription.plan === 'free' && !subscriptionError && (
            <Alert variant="info" className="mb-3">
              <div className="d-flex align-items-center">
                <div>
                  <strong>{t('settings.subscription.freePlan.title')}:</strong> {t('settings.subscription.freePlan.partUsage', { remaining: getRemainingParts(), total: subscription.partLimit })}
                </div>
              </div>
            </Alert>
          )}

          {/* Errore di abbonamento */}
          {subscriptionError && (
            <Alert variant="danger" className="mb-3">
              <div className="d-flex align-items-center">
                <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                <div>
                  <strong>{t('settings.subscription.errors.title')}:</strong> {subscriptionError}
                </div>
              </div>
            </Alert>
          )}
          
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>{t('inventory.partForm.fields.brand')}*</Form.Label>
                <Form.Control
                  type="text"
                  name="brand"
                  value={formData.brand}
                  onChange={handleChange}
                  required
                  disabled={subscriptionError}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>{t('inventory.partForm.fields.model')}*</Form.Label>
                <Form.Control
                  type="text"
                  name="model"
                  value={formData.model}
                  onChange={handleChange}
                  required
                  disabled={subscriptionError}
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>{t('inventory.partForm.fields.type')}*</Form.Label>
            <Form.Select
              name="type"
              value={formData.type}
              onChange={handleChange}
              required
              disabled={subscriptionError}
            >
              <option value="">{t('inventory.partForm.fields.selectType')}</option>
              {PART_TYPES.map(type => (
                <option key={type.value} value={type.value}>
                  {t(type.translationKey)}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>{t('inventory.partForm.fields.quantity')}*</Form.Label>
                <Form.Control
                  type="number"
                  name="quantity"
                  value={formData.quantity}
                  onChange={handleChange}
                  min="0"
                  required
                  disabled={subscriptionError}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>{t('inventory.partForm.fields.minThreshold')}</Form.Label>
                <Form.Control
                  type="number"
                  name="minThreshold"
                  value={formData.minThreshold}
                  onChange={handleChange}
                  min="0"
                  disabled={subscriptionError}
                />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            <FontAwesomeIcon icon={faTimes} className="me-2" />
            {t('inventory.partForm.buttons.cancel')}
          </Button>
          <Button 
            variant="primary" 
            type="submit" 
            disabled={loading || subscriptionError}
          >
            <FontAwesomeIcon 
              icon={loading ? faSpinner : faSave} 
              className="me-2" 
              spin={loading} 
            />
            {loading ? t('inventory.partForm.buttons.saving') : 
             t('inventory.partForm.buttons.save')}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

export default PartForm; 